import React from "react";
import { useFormValidation, FormState } from "ui/hooks/use-form-validation";
import { Modal, FormElement } from "ui/components";
import { User } from "core/models/User";
import usePermissions from "ui/hooks/usePermissions";
import * as userRoles from "utils/userRoles";
import { useSessionContext } from "ui/context/SessionContext";
import { useNotifications } from "ui/context/NotificationsContext";
import { includeInProject, setRoles } from "core/services/site/user";

interface Props {
    type: "add" | "edit";
    onClose(): void;
    onSave(): void;
    editableUser?: User;
}

const UserModal: React.FC<Props> = ({
    type,
    onClose,
    onSave,
    editableUser,
}) => {
    const sessionContext = useSessionContext();
    const userPermissions = usePermissions(editableUser?.roles);
    const { addNotification } = useNotifications();
    const [pending, setPending] = React.useState<boolean>(false);
    const [allRoles, setAllRoles] = React.useState<string[]>([]);
    const userHasSharePermissions = (): boolean => {
        if (!editableUser) {
            return false;
        }
        return (
            userPermissions.hasRightToConfigureShare &&
            userPermissions.hasRightToShare
        );
    };
    const userCanManageAddons = (): boolean => {
        if (!editableUser) {
            return false;
        }
        return userPermissions.canManageAddons;
    };
    const userIsProjectAdmin = (): boolean => {
        if (!editableUser) {
            return false;
        }
        return userPermissions.isProjectAdmin;
    };

    React.useEffect(() => {
        if (!sessionContext.session || !sessionContext.selectedProject) {
            return;
        }
        setAllRoles([
            userRoles.canCreateShare(sessionContext.selectedProject.key),
            userRoles.canConfigureShare(sessionContext.selectedProject.key),
            userRoles.canShare(sessionContext.selectedProject.key),
            userRoles.canManageAddons(sessionContext.selectedProject.key),
            userRoles.isProjectAdmin(sessionContext.selectedProject.key),
        ]);
    }, []);

    const defaultFormState: FormState = {
        email: {
            valid: true,
            touched: false,
            value: type === "edit" && editableUser ? editableUser.email : "",
            errorMsg: "",
            title: "Email",
            defaultText: "Enter valid email address",
            type: "text",
            rules: {
                required: true,
                isEmail: true,
            },
        },
        permissionSocial: {
            valid: true,
            touched: false,
            value: userHasSharePermissions().toString(),
            errorMsg: "",
            title: "Manage social media accounts",
            type: "checkbox",
            rules: {},
        },
        permissionAddons: {
            valid: true,
            touched: false,
            value: userCanManageAddons().toString(),
            errorMsg: "",
            title: "Manage Addons",
            type: "checkbox",
            rules: {},
        },
        permissionUsers: {
            valid: true,
            touched: false,
            value: userIsProjectAdmin().toString(),
            errorMsg: "",
            type: "checkbox",
            title: `Manage project's users and livestreams`,
            rules: {},
        },
    };

    const theForm = useFormValidation(defaultFormState);

    const handleSave = async () => {
        setPending(true);
        let newRoles: string[] = [];
        if (!sessionContext.selectedProject) {
            return;
        }
        if (theForm.formState.permissionSocial.value === "true") {
            newRoles = newRoles.concat([
                userRoles.canCreateShare(sessionContext.selectedProject.key),
                userRoles.canConfigureShare(sessionContext.selectedProject.key),
                userRoles.canShare(sessionContext.selectedProject.key),
            ]);
        }
        if (theForm.formState.permissionAddons.value === "true") {
            newRoles = newRoles.concat([
                userRoles.canManageAddons(sessionContext.selectedProject.key),
            ]);
        }
        if (theForm.formState.permissionUsers.value === "true") {
            newRoles = newRoles.concat([
                userRoles.isProjectAdmin(sessionContext.selectedProject.key),
            ]);
        }
        const disabledRoles = allRoles.filter(
            (role) => !newRoles.includes(role),
        );

        try {
            if (!sessionContext.session) {
                throw Error("no session");
            }
            if (type === "edit") {
                if (!editableUser?.id) {
                    throw Error("no editable user");
                }
                const result = await setRoles(
                    sessionContext.session.token,
                    sessionContext.selectedProject.id,
                    editableUser.id,
                    newRoles,
                    disabledRoles,
                );
                if (result.error) {
                    throw Error(result.error);
                }
                addNotification(
                    "success",
                    "User Edited",
                    `update permissions for ${theForm.formState.email.value}`,
                    5000,
                );
            } else {
                if (!theForm.formState.email.value) {
                    throw Error("no email found");
                }
                const result = await includeInProject(
                    sessionContext.session.token,
                    sessionContext.selectedProject.id,
                    theForm.formState.email.value.toString(),
                    newRoles,
                    disabledRoles,
                );
                if (result.error) {
                    throw Error(result.error);
                }
                addNotification(
                    "success",
                    "Added new user",
                    `${theForm.formState.email.value} can check his/her email`,
                    5000,
                );
            }
            setPending(false);
            onSave();
        } catch (err) {
            addNotification(
                "error",
                "Error while saving user",
                (err as Error).message,
                5000,
            );
            setPending(false);
        }
    };

    const { formState } = theForm;

    const getSaveButtonText = (): string => {
        if (pending) {
            return "Saving user...";
        }
        if (!pending && type === "edit") {
            return "Edit";
        }
        return "Create ";
    };

    const modalContent = Object.keys(formState).map((fieldKey) => (
        <FormElement
            theme="light"
            defaultText={formState[fieldKey].defaultText}
            type={formState[fieldKey].type || "text"}
            title={formState[fieldKey].title || ""}
            id={fieldKey}
            key={fieldKey}
            fieldState={formState[fieldKey]}
            onChange={(value) => {
                theForm.inputChangeHandler(fieldKey, value);
            }}
            disabled={pending}
            options={formState[fieldKey].options}
        />
    ));

    return (
        <Modal
            content={modalContent}
            lineColor="amber"
            theme="light"
            onSave={handleSave}
            onClose={onClose}
            pending={pending ? "saving user" : undefined}
            saveButtonDisabled={!theForm.formValid || pending}
            saveButtonText={getSaveButtonText()}
            title={
                type === "add"
                    ? "Add User"
                    : `Edit User: ${editableUser?.username}`
            }
            icon={type === "add" ? "plus" : "edit"}
        />
    );
};
export default UserModal;
